import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import type { CmsBettingVendor } from '@/types/cms/bettingVendor'
import type { VoucherResponse } from '@/types/pos/deposit'

// Types
export type VoucherType = 'gaming' | 'betting' | 'ips' | null
export type PaymentMethod = 'cash' | 'card'

export type BasketProduct = {
    id: number
    name: string
    slug?: string
    sku?: string
    price?: number
    imageUrl?: string | null
}

export type BasketItem = {
    product: BasketProduct
    qty: number
}

export type BasketState = Record<string, BasketItem>

export interface OrderState {
    currentStep: VoucherPurchaseStep
    sessionId: string | null
    voucherType: VoucherType | null
    paymentMethod: PaymentMethod | null
    amount: number | null
    transactionId: string | null
    timestamp: number | null
    error: string | null
    shouldShowAreYouThere: boolean
    basket: BasketState
    selectedBettingVendor: CmsBettingVendor | null
    directBettingFailedAttempts: number
    // Direct-betting flow continuity: persisted in the order context so the
    // user can switch vendor/account details without losing the already
    // created voucher.
    createdVoucherData: VoucherResponse | null
    directBettingAccountId: string | null
    // True after a direct-betting deposit has succeeded. The voucher is then
    // spent and must not be retried against another vendor/account.
    directBettingCompleted: boolean
    // Set to true the first time a draft deposit amount > 0 is detected in
    // this session. Used to skip the payment-selection screen instantly
    // without waiting for the slow GetVoucherAmount API call.
    cashDetected: boolean
}

export interface OrderContextType {
    state: OrderState
    setCurrentStep: (step: VoucherPurchaseStep) => void
    setSessionId: (sessionId: string) => void
    setVoucherType: (voucherType: VoucherType) => void
    setPaymentMethod: (paymentMethod: PaymentMethod) => void
    setAmount: (amount: number) => void
    setTransactionId: (transactionId: string) => void
    setError: (error: string | null) => void
    addToBasket: (item: BasketProduct) => void
    removeFromBasket: (item: BasketProduct) => void
    clearBasket: () => void
    resetOrder: () => void
    updateOrderData: (data: Partial<OrderState>) => void
    setShouldShowAreYouThere: (shouldShow: boolean) => void
    setSelectedBettingVendor: (vendor: CmsBettingVendor | null) => void
    setDirectBettingFailedAttempts: (attempts: number) => void
    setCreatedVoucherData: (voucher: VoucherResponse | null) => void
    setDirectBettingAccountId: (accountId: string | null) => void
    setDirectBettingCompleted: (completed: boolean) => void
    setCashDetected: (detected: boolean) => void
}

// Action types
export type OrderAction =
    | { type: 'SET_CURRENT_STEP'; payload: VoucherPurchaseStep }
    | { type: 'SET_SESSION_ID'; payload: string }
    | { type: 'SET_VOUCHER_TYPE'; payload: VoucherType }
    | { type: 'SET_PAYMENT_METHOD'; payload: PaymentMethod }
    | { type: 'SET_AMOUNT'; payload: number }
    | { type: 'SET_TRANSACTION_ID'; payload: string }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'ADD_BASKET_ITEM'; payload: BasketProduct }
    | { type: 'REMOVE_BASKET_ITEM'; payload: BasketProduct }
    | { type: 'CLEAR_BASKET' }
    | { type: 'UPDATE_ORDER_DATA'; payload: Partial<OrderState> }
    | { type: 'RESET_ORDER' }
    | { type: 'SET_SHOULD_SHOW_ARE_YOU_THERE'; payload: boolean }
    | { type: 'SET_SELECTED_BETTING_VENDOR'; payload: CmsBettingVendor | null }
    | { type: 'SET_DIRECT_BETTING_FAILED_ATTEMPTS'; payload: number }
    | { type: 'SET_CREATED_VOUCHER_DATA'; payload: VoucherResponse | null }
    | { type: 'SET_DIRECT_BETTING_ACCOUNT_ID'; payload: string | null }
    | { type: 'SET_DIRECT_BETTING_COMPLETED'; payload: boolean }
    | { type: 'SET_CASH_DETECTED'; payload: boolean }

// Initial state
export const initialOrderState: OrderState = {
    currentStep: VoucherPurchaseStep.HOME_START,
    sessionId: null,
    voucherType: null,
    paymentMethod: null,
    amount: null,
    transactionId: null,
    timestamp: null,
    error: null,
    shouldShowAreYouThere: false,
    basket: {},
    selectedBettingVendor: null,
    directBettingFailedAttempts: 0,
    createdVoucherData: null,
    directBettingAccountId: null,
    directBettingCompleted: false,
    cashDetected: false
}
